/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { SchedulerTechnician } from '@/types/scheduler';

export type TechnicianStatusFilter = 'all' | 'active' | 'inactive';

export interface TechnicianListFilters {
  q: string;
  status: TechnicianStatusFilter;
  expertise: string;
  capacityMin: string;
  capacityMax: string;
  sort: number;
}

export interface AdvancedTechnicianFilters {
  expertise: string[];
  capacityMin: string;
  capacityMax: string;
}

export interface TechnicianFilterOptions {
  expertiseNames: string[];
}

export type TechnicianSortOption = {
  label: string;
  sortBy: 'createdAt' | 'updatedAt' | 'name' | 'capacityPerDay';
  sortDir: 'asc' | 'desc';
  icon: ReactNode;
};

export const DEFAULT_TECHNICIAN_LIST_FILTERS: TechnicianListFilters = {
  q: '',
  status: 'all',
  expertise: '',
  capacityMin: '',
  capacityMax: '',
  sort: 0,
};

export const TECHNICIAN_STATUS_FILTERS: Array<{ value: TechnicianStatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const TECHNICIAN_STATUS_DOT_COLORS: Record<string, string> = {
  active: 'bg-emerald-500',
  inactive: 'bg-zinc-400',
};

export const TECHNICIAN_SORT_OPTIONS: TechnicianSortOption[] = [
  { label: 'Created', sortBy: 'createdAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Created', sortBy: 'createdAt', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Updated', sortBy: 'updatedAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Name', sortBy: 'name', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Capacity', sortBy: 'capacityPerDay', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Capacity', sortBy: 'capacityPerDay', sortDir: 'asc', icon: <ArrowUp size={12} /> },
];

export function resolveTechnicianListFilters(
  search: Partial<TechnicianListFilters>,
): TechnicianListFilters {
  return {
    q: search.q ?? DEFAULT_TECHNICIAN_LIST_FILTERS.q,
    status: search.status ?? DEFAULT_TECHNICIAN_LIST_FILTERS.status,
    expertise: search.expertise ?? DEFAULT_TECHNICIAN_LIST_FILTERS.expertise,
    capacityMin: search.capacityMin ?? DEFAULT_TECHNICIAN_LIST_FILTERS.capacityMin,
    capacityMax: search.capacityMax ?? DEFAULT_TECHNICIAN_LIST_FILTERS.capacityMax,
    sort: search.sort ?? DEFAULT_TECHNICIAN_LIST_FILTERS.sort,
  };
}

export function splitParam(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function encodeParamList(values: string[]): string {
  return values.join(',');
}

export function toAdvancedFilters(filters: TechnicianListFilters): AdvancedTechnicianFilters {
  return {
    expertise: splitParam(filters.expertise),
    capacityMin: filters.capacityMin,
    capacityMax: filters.capacityMax,
  };
}

export function countAdvancedFilters(filters: AdvancedTechnicianFilters): number {
  let count = 0;
  if (filters.expertise.length) count++;
  if (filters.capacityMin) count++;
  if (filters.capacityMax) count++;
  return count;
}

export function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

export function getTechExpertise(
  tech: SchedulerTechnician,
): Array<{ serviceType?: { name?: string } }> {
  return (
    ((tech as unknown as Record<string, unknown>).expertise as Array<{
      serviceType?: { name?: string };
    }>) ?? []
  );
}

export function extractTechnicianFilterOptions(
  technicians: SchedulerTechnician[],
): TechnicianFilterOptions {
  const expertiseSet = new Set<string>();

  for (const technician of technicians) {
    for (const expertise of getTechExpertise(technician)) {
      if (expertise.serviceType?.name) expertiseSet.add(expertise.serviceType.name);
    }
  }

  return {
    expertiseNames: [...expertiseSet].sort(),
  };
}

export function technicianMatchesFilters(
  technician: SchedulerTechnician,
  filters: TechnicianListFilters,
): boolean {
  if (filters.status === 'active' && !technician.isActive) return false;
  if (filters.status === 'inactive' && technician.isActive) return false;

  const q = filters.q.trim().toLowerCase();
  if (
    q &&
    !technician.name.toLowerCase().includes(q) &&
    !technician.email.toLowerCase().includes(q)
  ) {
    return false;
  }

  const advanced = toAdvancedFilters(filters);
  if (advanced.expertise.length > 0) {
    const names = getTechExpertise(technician).map(
      (expertise) => expertise.serviceType?.name ?? '',
    );
    if (!advanced.expertise.some((expertise) => names.includes(expertise))) return false;
  }

  if (advanced.capacityMin && technician.capacityPerDay < Number(advanced.capacityMin))
    return false;
  if (advanced.capacityMax && technician.capacityPerDay > Number(advanced.capacityMax))
    return false;

  return true;
}

export function sortTechnicians(
  technicians: SchedulerTechnician[],
  option: TechnicianSortOption,
): SchedulerTechnician[] {
  return [...technicians].sort((a, b) => {
    const left = a[option.sortBy] ?? '';
    const right = b[option.sortBy] ?? '';
    const comparison =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left).localeCompare(String(right));
    return option.sortDir === 'asc' ? comparison : -comparison;
  });
}
